import React, { Component } from 'react';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import { connect } from 'react-redux';

import ProductsGridItemWithIntersectionDetection
  from './ProductsGridItemWithIntersectionDetection.jsx';
import EmailBack from './EmailBack.jsx';
import ProductBriefDescription from './ProductBriefDescription.jsx';
import ProductFlexibleCreditTooltip, { gridViewPositionHandler }
  from './ProductFlexibleCreditTooltip.jsx';


class ProductGridRow extends Component {
  constructor(props) {
    super(props);

    // it stores all <ProductsGridItem>s DOM elements in order to gather height of their elements
    this.gridRowDOMElements = [];

    this.state = {
      numberOfUnloadedImagesInRow: this.getNumberOfImagesInRow(),
      shouldGridItemsInformAboutUpdate: false,
      numberOfUpdatedGridItems: 0,
      dynamicHeightsOfGridElements: {}
    };

    this.imageOfGridItemLoaded = this.imageOfGridItemLoaded.bind(this);
    this.gridItemUpdated = this.gridItemUpdated.bind(this);
    this.setFlexibleCreditAnchorRef = this.setFlexibleCreditAnchorRef.bind(this);
  }

  componentWillReceiveProps(nextProps) {
    // if following condition is true, each grid item has to re-calculate size of its elements
    // and inform about update this parent component. Each subcondition is described below
    if (
      // if windows size change
      (this.props.viewportWidth !== nextProps.viewportWidth) ||

      // when postcodechecker finishes searching for channels availabilities for a new postcode
      (this.props.pendingLocationRequest && !nextProps.pendingLocationRequest) ||

      // when location info disappears or appears
      // after clicking on changeLocation or searching new location
      (this.props.isLocationInfoVisible !== nextProps.isLocationInfoVisible)
    ) {
      this.setState(
        {
          shouldGridItemsInformAboutUpdate: true,
          dynamicHeightsOfGridElements: {}
        }
      );
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    // before all images are loaded, it's not necessary to re-render
    if (nextState.numberOfUnloadedImagesInRow > 0) {
      return false;
    }

    // before all grid items inform about their updates, it's not necessary to re-render
    if (nextState.numberOfUpdatedGridItems > 0 &&
      nextState.numberOfUpdatedGridItems < this.gridRowDOMElements.length) {
      return false;
    }

    return true;
  }

  setFlexibleCreditAnchorRef(ref) {
    this.flexibleCreditAnchorRef = ref;
  }

  getFlexibleTooltipArrowDirection() {
    if (this.props.deviceType === 'desktop' || this.props.deviceType === 'wide') {
      return 'right';
    }
    return 'bottom';
  }

  // returns number of all images in <ProductsGridItem> in this row
  // which influences height of grid item
  getNumberOfImagesInRow() {
    let numberOfImagesInRow = 0;

    this.props.rowProductsList.forEach((currentProduct) => {
      numberOfImagesInRow += 1 /* 1 for product image */ +
        (currentProduct.icons ? currentProduct.icons.length : 0) +
        (currentProduct.promoMessages.badges ? currentProduct.promoMessages.badges.length : 0);
    });

    return numberOfImagesInRow;
  }

  // Gets max height of elements in grid items
  // in order to keep same height in all <ProductsGridItem> elements
  getHeights() {
    return {
      productImagesHeight: this.getMaxHeightOfElementInGridItems('.product-images'),
      iconsHeight: this.getMaxHeightOfElementInGridItems('.badges'),
      countdownLabelsHeight: this.getMaxHeightOfElementInGridItems('.countdownLabels'),
      productBundleHeight: this.getMaxHeightOfElementInGridItems('.productBundle'),
      reevooPlaceholderHeight: this.getMaxHeightOfElementInGridItems('.reevoo-placeholder'),
      promoMessagesHeight: this.getMaxHeightOfElementInGridItems('.promoMessages'),
      productDescriptionHeight: this.getMaxHeightOfElementInGridItems('.productDescription'),
      productPricesHeight: this.getMaxHeightOfElementInGridItems('.productPrices'),
      onlineCreditMessageWrapperHeight:
        this.getMaxHeightOfElementInGridItems('.online-credit-message-wrapper'),
      alternativeTariffLinkWrapperHeight:
        this.getMaxHeightOfElementInGridItems('.alternative-tariff-link-wrapper'),
      countdownMessageHeight: this.getMaxHeightOfElementInGridItems('.countdownMessage'),
      channelsAvailabilityHeight: this.getMaxHeightOfElementInGridItems('.channels-availability'),
      productDescHeight: this.getMaxHeightOfElementInGridItems('.product-desc')
    };
  }

  getMaxHeightOfElementInGridItems(elementSelector) {
    return Math.max(
      ...this.gridRowDOMElements.map(
        // ReactDOM.findDOMNode() is used here only for gathering element height.
        // It doesn't change DOM
        element => ReactDOM.findDOMNode(element).querySelector(elementSelector).clientHeight // eslint-disable-line react/no-find-dom-node
      )
    );
  }

  imageOfGridItemLoaded() {
    // functional setState is used here to make setState synchronous
    this.setState((previousState) => {
      const newNumberOfUnloadedImagesInRow = previousState.numberOfUnloadedImagesInRow - 1;
      // all <ProductsGridItem>s images were loaded so it's possible to re-render
      if (newNumberOfUnloadedImagesInRow === 0) {
        return {
          ...previousState,
          dynamicHeightsOfGridElements: this.getHeights(),
          numberOfUnloadedImagesInRow: newNumberOfUnloadedImagesInRow
        };
      }

      // not all <ProductsGridItem>s images were loaded so no re-render
      return {
        ...previousState,
        numberOfUnloadedImagesInRow: newNumberOfUnloadedImagesInRow
      };
    });
  }

  gridItemUpdated() {
    this.setState((previousState) => {
      // all <ProductsGridItem>s were updated so it's possible to re-render
      if (previousState.numberOfUpdatedGridItems + 1 === this.gridRowDOMElements.length) {
        return {
          ...previousState,
          dynamicHeightsOfGridElements: this.getHeights(),
          shouldGridItemsInformAboutUpdate: false,
          numberOfUpdatedGridItems: 0
        };
      }

      // not all <ProductsGridItem>s were updated so no re-render
      return {
        ...previousState,
        numberOfUpdatedGridItems: previousState.numberOfUpdatedGridItems + 1
      };
    });
  }

  render() {
    const productGridRow = [];
    const props = this.props;

    // It has to be made empty before re-render
    this.gridRowDOMElements = [];

    props.rowProductsList.forEach((currentProduct) => {
      productGridRow.push(
        <ProductsGridItemWithIntersectionDetection
          {...currentProduct}
          {...this.state.dynamicHeightsOfGridElements}
          countdownClassSuffix={props.countdownClassSuffix}
          key={currentProduct.id}
          translations={props.translations}
          onImageLoaded={() => this.imageOfGridItemLoaded()}
          ref={(gridItemDOMElement) => {
            // ref callback is returns null if component is unmounted,
            // that's why this checking is here in order not to add nulls to the array
            if (gridItemDOMElement) {
              this.gridRowDOMElements.push(gridItemDOMElement);
            }
          }}
          setFlexibleAnchorRefToParent={this.setFlexibleCreditAnchorRef}
          shouldInformAboutUpdate={this.state.shouldGridItemsInformAboutUpdate}
          gridItemUpdated={this.gridItemUpdated}
          dataInteraction={props.dataInteraction}
          onIntersection={props.onProductIntersection}
        />
      );
    });

    // productDescription
    const productWithOpenedDescription = props.rowProductsList
      .find(product => product.id === props.briefDescriptionProductID);

    if (productWithOpenedDescription) {
      productGridRow.push(
        <ProductBriefDescription
          icons={productWithOpenedDescription.icons}
          descriptionShortened={productWithOpenedDescription.descriptionShortened}
          link={productWithOpenedDescription.link}
          productDescriptionLinkTranslation={props.translations.productDescriptionLinkTranslation}
          key={`brief-description-${productWithOpenedDescription.id}`}
        />
      );
    }

    // emailMeBack form
    if (props.rowProductsList
      .find(product => product.id === props.emailWhenBackFormProductID)) {
      productGridRow.push(
        <EmailBack
          translations={props.translations.emailWhenBackForm}
          key={`email-back-${props.emailWhenBackFormProductID}`}
        />
      );
    }

    // Flexible credit modal tooltip
    const isProductInRow = props.rowProductsList
      .find(product => product.id === props.flexibleCreditTooltipProductID);

    if (isProductInRow && this.flexibleCreditAnchorRef) {
      let tooltipWidth = null;
      if (this.props.deviceType === 'tablet') {
        tooltipWidth = this.flexibleCreditAnchorRef.getBoundingClientRect().width;
      }

      productGridRow.push(
        <ProductFlexibleCreditTooltip
          productID={this.props.flexibleCreditTooltipProductID}
          arrowDirection={this.getFlexibleTooltipArrowDirection()}
          flexibleCreditAnchorRef={this.flexibleCreditAnchorRef}
          styleWidthPx={tooltipWidth}
          positionHandler={gridViewPositionHandler}
          key={`flexible-credit-tooltip-${props.flexibleCreditTooltipProductID}`}
        />
      );
    }

    return productGridRow;
  }
}

ProductGridRow.propTypes = {
  rowProductsList: PropTypes.arrayOf(PropTypes.object).isRequired,
  viewportWidth: PropTypes.number.isRequired,
  pendingLocationRequest: PropTypes.bool.isRequired,
  isLocationInfoVisible: PropTypes.bool.isRequired,
  translations: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.objectOf(PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.object
    ]))
  ]).isRequired,
  countdownClassSuffix: PropTypes.string,
  briefDescriptionProductID: PropTypes.string,
  emailWhenBackFormProductID: PropTypes.string,
  flexibleCreditTooltipProductID: PropTypes.string,
  deviceType: PropTypes.string.isRequired,
  dataInteraction: PropTypes.string,
  onProductIntersection: PropTypes.func
};

ProductGridRow.defaultProps = {
  countdownClassSuffix: null,
  briefDescriptionProductID: null,
  emailWhenBackFormProductID: null,
  flexibleCreditTooltipProductID: null,
  dataInteraction: undefined,
  onProductIntersection: undefined
};

function mapStateToProps(state) {
  return {
    countdownClassSuffix: state.listingPage.pageSetup.countdownClassSuffix,
    briefDescriptionProductID: state.listingPage.products.briefDescriptionProductID,
    emailWhenBackFormProductID: state.listingPage.products.emailWhenBackFormProductID,
    viewportWidth: state.general.viewportWidth,
    pendingLocationRequest: state.listingPage.pageSetup.xhrStatus.pendingLocationRequest,
    isLocationInfoVisible: !!state.listingPage.locationSearch.currentFoundLocation,
    flexibleCreditTooltipProductID: state.listingPage.products.flexibleCreditTooltipProductID,
    deviceType: state.general.device.deviceType
  };
}

export default connect(mapStateToProps)(ProductGridRow);
